/**
 * useAttendanceFilters.js — Puantaj filtre state yönetimi
 * Dropdown menüler için filtre state'i, uygulama/sıfırlama mantığı
 */
import { useState, useCallback } from 'react';
import { monthStartISO, todayISO } from '../utils/attendanceHelpers';

const DEFAULT_FILTERS = {
  query: '',
  department: 'ALL',
  schedule_name: 'ALL',
  schedule_type: 'ALL',
  status: 'ALL',
  approval: 'ALL',
  from_date: monthStartISO(),
  to_date: todayISO(),
};

export default function useAttendanceFilters(initial = {}) {
  const [filters, setFilters] = useState({ ...DEFAULT_FILTERS, ...initial });
  const [draft, setDraft] = useState({ ...DEFAULT_FILTERS, ...initial });
  const [activeFilterMenu, setActiveFilterMenu] = useState(null);

  /** Filtre menüsünü aç/kapat, açarken draft'ı güncelle */
  const openFilterMenu = useCallback((menuKey) => {
    setDraft(filters);
    setActiveFilterMenu((prev) => (prev === menuKey ? null : menuKey));
  }, [filters]);

  /** Draft'taki filtreleri uygula */
  const applyFilters = useCallback(() => {
    setFilters(draft);
    setActiveFilterMenu(null);
  }, [draft]);

  /** Filtreleri varsayılana sıfırla */
  const resetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    setDraft(DEFAULT_FILTERS);
    setActiveFilterMenu(null);
  }, []);

  /** Belirli bir filtre menüsünün aktif filtreleri olup olmadığını kontrol et */
  const hasActiveFilter = useCallback((menuKey) => {
    switch (menuKey) {
      case 'personnel':
        return Boolean(filters.query) || filters.department !== 'ALL';
      case 'date':
        return filters.from_date !== monthStartISO() || filters.to_date !== todayISO();
      case 'schedule':
        return filters.schedule_name !== 'ALL' || filters.schedule_type !== 'ALL';
      case 'status':
        return filters.status !== 'ALL';
      case 'approval':
        return filters.approval !== 'ALL';
      default:
        return false;
    }
  }, [filters]);

  /** Tek bir filtre alanını güncelle */
  const setFilter = useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  /** Draft'ta tek bir alanı güncelle */
  const setDraftField = useCallback((key, value) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  }, []);

  /** Aktif filtre sayısı */
  const activeFilterCount = [
    filters.department !== 'ALL',
    Boolean(filters.query),
    filters.schedule_name !== 'ALL',
    filters.schedule_type !== 'ALL',
    filters.status !== 'ALL',
    filters.approval !== 'ALL',
    filters.from_date !== monthStartISO(),
    filters.to_date !== todayISO(),
  ].filter(Boolean).length;

  /** API için params objesi oluştur */
  const buildApiParams = useCallback(() => {
    return {
      from_date: filters.from_date || undefined,
      to_date: filters.to_date || undefined,
      department: filters.department !== 'ALL' ? filters.department : undefined,
      query: filters.query || undefined,
    };
  }, [filters]);

  return {
    filters,
    draft,
    activeFilterMenu,
    activeFilterCount,
    openFilterMenu,
    applyFilters,
    resetFilters,
    hasActiveFilter,
    setFilter,
    setDraftField,
    buildApiParams,
    setActiveFilterMenu,
  };
}